import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";

const LOGO_LIGHT = logoLight.url;

// Landing-only palette (mirrors src/routes/index.tsx)
const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const INK = "#14213A";
const INK_2 = "#545C6B";
const INK_MUTED = "#6B7384";
const LINE = "#E4DECF";
const NAVY_TEXT_DIM = "#B6BFCD";
const NAVY_TEXT_DIM_2 = "#9AA5B4";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CATEGORY_OPTIONS = [
  { value: "question", label: "Question" },
  { value: "billing", label: "Billing" },
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature request" },
  { value: "account", label: "Account" },
  { value: "other", label: "Other" },
] as const;

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact CapitalDart" },
      {
        name: "description",
        content:
          "Get in touch with the CapitalDart team. Send us a note about your raise, pricing, or platform questions and we will reply by email.",
      },
      { property: "og:title", content: "Contact CapitalDart" },
      {
        property: "og:description",
        content:
          "Get in touch with the CapitalDart team. Send us a note about your raise, pricing, or platform questions and we will reply by email.",
      },
      { property: "og:url", content: "https://capitaldart.com/contact" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Contact CapitalDart" },
      {
        name: "twitter:description",
        content:
          "Get in touch with the CapitalDart team. Send us a note about your raise, pricing, or platform questions and we will reply by email.",
      },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <main
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased"
    >
      <ContactNav />
      <ContactSection />
      <ContactFooter />
    </main>
  );
}

function ContactNav() {
  const { session, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const isAuthed = Boolean(session);
  return (
    <header
      style={{
        background: NAVY,
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        position: "relative",
      }}
    >
      <style>{`
        @media (max-width: 1023.98px) {
          .cd-contactnav-desktop { display: none !important; }
          .cd-contactnav-mobile { display: inline-flex !important; }
        }
      `}</style>
      <div
        className="mx-auto"
        style={{
          maxWidth: 1200,
          padding: "18px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center" }}>
          <Logo
            src={LOGO_LIGHT}
            alt="CapitalDart"
            height={34}
            style={{ display: "block" }}
            trademarkClassName="text-[#F4F1E8]"
          />
        </Link>
        <nav
          className="cd-contactnav-desktop"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          {loading ? null : isAuthed ? (
            <>
              <Link
                to="/dashboard"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={async () => {
                  await signOut();
                  navigate({ to: "/" });
                }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  textDecoration: "none",
                }}
              >
                Back to home
              </Link>
              <Link
                to="/auth"
                search={{ mode: "signup" as const }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Get started
              </Link>
            </>
          )}
        </nav>
        <MobileNav className="cd-contactnav-mobile" />
      </div>
    </header>
  );
}

function ContactSection() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState<string>("question");
  const [message, setMessage] = useState("");
  // Honeypot — should remain empty for real users
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const trimmedEmail = email.trim();
  const trimmedName = name.trim();
  const trimmedMessage = message.trim();
  const emailValid = EMAIL_RE.test(trimmedEmail) && trimmedEmail.length <= 254;
  const canSubmit =
    !submitting &&
    emailValid &&
    trimmedMessage.length >= 1 &&
    trimmedMessage.length <= 2000 &&
    trimmedName.length <= 120;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    if (website.trim().length > 0) {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    const categoryLabel = CATEGORY_OPTIONS.find((c) => c.value === category)?.label ?? "Question";
    let ok = false;
    try {
      const { error } = await supabase.from("marketing_inquiries").insert({
        email: trimmedEmail.toLowerCase(),
        question: trimmedMessage,
        name: trimmedName || null,
        source: "marketing_contact_page",
        category: categoryLabel,
      } as never);
      ok = !error;
    } catch {
      ok = false;
    }
    setSubmitting(false);

    if (!ok) {
      toast.error("Could not send your message. Please email support@capitaldart.com directly.");
      return;
    }
    toast.success("Thanks, we will get back to you by email shortly.");
    setSubmitted(true);
    setName("");
    setEmail("");
    setMessage("");
    setCategory("question");
  }

  return (
    <section
      style={{ background: PAPER_2, padding: "88px 0 112px", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 760, padding: "0 32px" }}>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 13,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: GOLD,
            marginBottom: 18,
          }}
        >
          Contact
        </div>
        <h1
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(36px,4.2vw,56px)",
            lineHeight: 1.05,
            letterSpacing: "-.01em",
            margin: 0,
          }}
        >
          Get in touch.
        </h1>
        <p
          style={{
            marginTop: 18,
            maxWidth: 620,
            color: INK_2,
            fontSize: 17,
            lineHeight: 1.6,
          }}
        >
          Questions about CapitalDart, pricing, or your deal? Send a note and we will reply by
          email. Prefer direct email?{" "}
          <a
            href="mailto:support@capitaldart.com"
            style={{
              color: INK,
              borderBottom: `2px solid ${GOLD}`,
              textDecoration: "none",
              paddingBottom: 2,
            }}
          >
            support@capitaldart.com
          </a>
          .
        </p>

        {submitted ? (
          <div
            style={{
              marginTop: 40,
              border: `1px solid ${LINE}`,
              background: PAPER,
              padding: "28px 28px",
              borderRadius: 12,
              fontSize: 15,
              color: INK,
              lineHeight: 1.6,
            }}
          >
            Thanks, your message is in. We will reply by email shortly.
            <div style={{ marginTop: 18 }}>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  color: INK,
                  background: "transparent",
                  border: `1px solid ${INK}`,
                  padding: "10px 18px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Send another message
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            style={{
              marginTop: 44,
              display: "flex",
              flexDirection: "column",
              gap: 22,
              position: "relative",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                width: 1,
                height: 1,
                overflow: "hidden",
              }}
            >
              <label htmlFor="cd-website">Website</label>
              <input
                id="cd-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <Field label="Name">
              <input
                type="text"
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                style={inputStyle}
              />
            </Field>

            <Field label="Email" required>
              <input
                type="email"
                required
                value={email}
                maxLength={254}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@firm.com"
                autoComplete="email"
                aria-invalid={email.length > 0 && !emailValid}
                style={inputStyle}
              />
              {email.length > 0 && !emailValid ? (
                <p style={{ fontSize: 12, color: "#B0442B", marginTop: 6 }}>
                  Enter a valid email address.
                </p>
              ) : null}
            </Field>

            <Field label="Category">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  style={{
                    ...inputStyle,
                    height: 44,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Message"
              required
              right={
                <span style={{ fontSize: 12, color: INK_MUTED, fontFamily: FONT_MONO }}>
                  {trimmedMessage.length} / 2000
                </span>
              }
            >
              <textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={7}
                maxLength={2000}
                placeholder="What can we help you with?"
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  minHeight: 160,
                  fontFamily: FONT_SANS,
                  lineHeight: 1.55,
                }}
              />
            </Field>

            <div style={{ paddingTop: 6 }}>
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 15,
                  fontWeight: 600,
                  color: NAVY_DEEP,
                  background: GOLD,
                  padding: "14px 28px",
                  border: "none",
                  borderRadius: 6,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  opacity: canSubmit ? 1 : 0.55,
                  transition: "background .15s",
                }}
              >
                {submitting ? "Sending..." : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: FONT_SANS,
  fontSize: 15,
  color: INK,
  background: PAPER,
  border: `1px solid ${LINE}`,
  borderRadius: 6,
  padding: "12px 14px",
  outline: "none",
};

function Field({
  label,
  required,
  right,
  children,
}: {
  label: string;
  required?: boolean;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <label
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: INK_MUTED,
            fontWeight: 500,
          }}
        >
          {label}
          {required ? <span style={{ color: "#B0442B", marginLeft: 6 }}>*</span> : null}
        </label>
        {right}
      </div>
      {children}
    </div>
  );
}

function ContactFooter() {
  return (
    <footer style={{ background: NAVY_DEEP, color: NAVY_TEXT_DIM_2, padding: "48px 0" }}>
      <div
        className="mx-auto"
        style={{
          maxWidth: 1200,
          padding: "0 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Logo
            src={LOGO_LIGHT}
            alt="CapitalDart"
            height={26}
            style={{ display: "block" }}
            trademarkClassName="text-[#F4F1E8]"
          />
          <span style={{ fontSize: 13.5, color: "#56627A", marginLeft: 14, fontFamily: FONT_SANS }}>
            © 2026
          </span>
        </div>
        <div style={{ display: "flex", gap: 26, fontSize: 14, fontFamily: FONT_SANS }}>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>
            Terms
          </Link>
          <Link to="/lists" style={{ color: "inherit", textDecoration: "none" }}>
            Top 10 Investor Rankings
          </Link>
          <Link to="/contact" style={{ color: "inherit", textDecoration: "none" }}>
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}
